import React, { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Timeline, Tag, Modal, Form, Select, Input, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { CheckOutlined, CloseOutlined, EyeOutlined } from '@ant-design/icons'
import { approvalsApi, documentsApi } from '@/api/services'
import type { Approval, UpdateApprovalRequest } from '@/types'
import { Button } from '@/components/ui/Button'
import { OTHERS_REASON, buildRemarks, reasonsFor } from '@/components/approvals/decisionReasons'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { PageShell, PageHero, DashboardPanel } from '@/components/dashboard'
import { FileSearch } from 'lucide-react'

const { Option } = Select
const { Text } = Typography
const { TextArea } = Input

function statusTag(status: string) {
  const color = status === 'Approved' ? 'green' : status === 'Rejected' ? 'red' : 'orange'
  return <Tag color={color}>{status}</Tag>
}

function getAttachmentHref(h: Approval): string | null {
  const anyH = h as any
  return (h.newUrl || h.url || anyH.tempUrl) ?? null
}

function isPdf(url: string) {
  return /\.pdf($|\?)/i.test(url)
}



const ApprovalDetailPage: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams()
  const id = Number(params.id)
  const qc = useQueryClient()
  const { user } = useAuthStore()
  // Approve/Reject is admin-only: POD Admin is the only role granted `pod.approve`
  // (see backend roleToPermissions). The API enforces this too; this just keeps
  // the controls from rendering for transporters / other roles.
  const canApprove = user?.permissions?.includes('pod.approve') ?? false

  const [decisionOpen, setDecisionOpen] = useState(false)
  const [decisionMode, setDecisionMode] = useState<'Approved' | 'Rejected'>('Approved')
  const [form] = Form.useForm()
  const watchedReason = Form.useWatch('reason', form)
  const remarksRequired = watchedReason === OTHERS_REASON

  const { data: approvalRes, isLoading: isApprovalLoading } = useQuery({
    queryKey: ['approval', id],
    queryFn: () => approvalsApi.getApprovalById(id),
    select: (res: any) => res.data,
    enabled: Number.isFinite(id) && id > 0,
  })

  const approval: Approval | undefined = (approvalRes as any)?.data

  const { data: historyRes, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['document-history', approval?.documentNo, approval?.type],
    queryFn: () => documentsApi.getHistory(String(approval?.documentNo), { type: approval?.type }),
    select: (res: any) => res.data,
    enabled: Boolean(approval?.documentNo),
  })

  const rows: Approval[] = ((historyRes as any)?.data ?? []) as Approval[]

  const header = useMemo(() => {
    const latest = rows[rows.length - 1] ?? approval
    return {
      latestStatus: latest?.status ?? 'Pending',
      type: approval?.type ?? latest?.type ?? null,
      customerName: latest?.customerName ?? null,
      fromLocation: latest?.fromLocation ?? null,
      toLocation: latest?.toLocation ?? null,
      count: rows.length,
    }
  }, [approval, rows])

  const latestAttachment = useMemo(() => {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const href = getAttachmentHref(rows[i])
      if (href) return href
    }
    return approval ? getAttachmentHref(approval) : null
  }, [approval, rows])

  const updateApprovalMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateApprovalRequest }) => approvalsApi.updateApproval(id, data),
    onSuccess: (_data, variables) => {
      const status = variables?.data?.status
      if (status === 'Approved') {
        toast.success('Approved successfully')
      } else if (status === 'Rejected') {
        toast.success('Rejected successfully')
      } else {
        toast.success('Approval updated successfully')
      }
      setDecisionOpen(false)
      form.resetFields()
      qc.invalidateQueries({ queryKey: ['approvals'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      qc.invalidateQueries({ queryKey: ['approval', id] })
      qc.invalidateQueries({ queryKey: ['approvals-reports'] })
      qc.invalidateQueries({ queryKey: ['statistics'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update approval')
    },
  })

  const openDecision = (p: 'Approved' | 'Rejected') => {
    setDecisionMode(p)
    setDecisionOpen(true)
    form.setFieldsValue({ reason: undefined, remarks: '' })
  }

  const submitDecision = (values: any) => {
    if (!approval) return
    updateApprovalMutation.mutate({
      id: approval.id,
      data: { status: decisionMode, remarks: buildRemarks(values.reason, values.remarks) },
    })
  }

  const openAttachment = () => {
    if (!latestAttachment) {
      toast.error('No attachment available')
      return
    }
    window.open(latestAttachment, '_blank', 'noopener,noreferrer')
  }

  const loading = isApprovalLoading || isHistoryLoading

  return (
    <PageShell>
      <PageHero
        title="Approval Review"
        subtitle={
          <>
            Document <span className="font-semibold text-foreground">{approval?.documentNo ?? '—'}</span> •{' '}
            {header.latestStatus ? statusTag(header.latestStatus) : null} • {header.count} event(s)
          </>
        }
        badges={[{ label: 'Review', accent: true, icon: FileSearch }]}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button variant="outline" onClick={openAttachment} disabled={!latestAttachment}>
              <EyeOutlined />
              View
            </Button>
            {canApprove && (
              <>
                <Button onClick={() => openDecision('Approved')} disabled={!approval || approval.status !== 'Pending'}>
                  <CheckOutlined />
                  Approve
                </Button>
                <Button variant="outline" onClick={() => openDecision('Rejected')} disabled={!approval || approval.status !== 'Pending'}>
                  <CloseOutlined />
                  Reject
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <DashboardPanel title="Summary" subtitle="Current snapshot" className="lg:col-span-4">
          <div className="space-y-3 text-sm">
              <div>
                <div className="text-muted-foreground">Type</div>
                <div className="mt-1">
                  {header.type ? (
                    <Tag color={header.type === 'ALT' ? 'blue' : header.type === 'DHR' ? 'green' : header.type === 'GRN' ? 'orange' : 'purple'}>
                      {header.type}
                    </Tag>
                  ) : (
                    '—'
                  )}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Customer</div>
                <div className="mt-1 text-foreground">{header.customerName ?? '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">From</div>
                <div className="mt-1 text-foreground">{header.fromLocation ?? '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">To</div>
                <div className="mt-1 text-foreground">{header.toLocation ?? '—'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Current Status</div>
                <div className="mt-1 text-foreground">{header.latestStatus ? statusTag(header.latestStatus) : '—'}</div>
              </div>
              {approval ? (
                <>
                  <div className="border-t border-border pt-3 space-y-3">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Stored record</div>
                    {[
                      ['Material code', approval.materialCodeNo],
                      ['Year', approval.year],
                      ['Posting date', approval.postingDate],
                      ['Customer code', approval.customerCode],
                      ['Quantity', approval.quantity],
                      ['Actual qty', approval.actual_qty],
                      ['Excess qty', approval.excess_qty],
                      ['Short qty', approval.short_qty],
                      ['Vehicle', approval.vehicleNo],
                      ['LR No', approval.lrNo],
                      ['Transporter code', approval.transporterCode],
                      ['Transporter', approval.transporterName],
                      ['Submitted by', approval.userName],
                    ].map(([label, val]) => (
                      <div key={String(label)}>
                        <div className="text-muted-foreground">{label}</div>
                        <div className="mt-1 text-foreground">{val != null && val !== '' ? String(val) : '—'}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
          </div>
        </DashboardPanel>

        <DashboardPanel title="History" subtitle="All status changes and attachments" className="lg:col-span-8">
              {loading ? (
                <div className="skeleton h-32 rounded-lg" />
              ) : rows.length ? (
                <Timeline
                  items={rows.map((h) => {
                    const href = getAttachmentHref(h)
                    return {
                      color: h.status === 'Approved' ? 'green' : h.status === 'Rejected' ? 'red' : 'orange',
                      children: (
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-foreground">{h.status}</span>
                            <span className="text-xs text-muted-foreground">{dayjs(h.createdAt).format('DD MMM YYYY, HH:mm')}</span>
                          </div>
                          <div className="text-sm text-muted-foreground">{h.approval_remark || h.rejection_remark || '—'}</div>
                          {href ? (
                            <div className="rounded-md border border-border bg-background overflow-hidden">
                              {isPdf(href) ? (
                                <iframe src={href} className="w-full h-[70vh]" title={`Attachment ${h.id}`} />
                              ) : (
                                <img src={href} loading="lazy" className="w-full max-h-[70vh] object-contain bg-background" alt={`Attachment ${h.id}`} />
                              )}
                            </div>
                          ) : null}
                        </div>
                      ),
                    }
                  })}
                />
              ) : (
                <div className="text-sm text-muted-foreground">No history found.</div>
              )}
        </DashboardPanel>
      </div>

      <Modal
        title={decisionMode === 'Approved' ? 'Approve Document' : 'Reject Document'}
        open={decisionOpen}
        onCancel={() => {
          setDecisionOpen(false)
          form.resetFields()
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        {approval ? (
          <Form form={form} layout="vertical" onFinish={submitDecision}>
            <div className="mb-4 p-4 bg-muted/30 rounded-lg border border-border">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <Text strong>Document No:</Text> {approval.documentNo}
                </div>
                <div>
                  <Text strong>Type:</Text>{' '}
                  <Tag color={approval.type === 'ALT' ? 'blue' : approval.type === 'DHR' ? 'green' : approval.type === 'GRN' ? 'orange' : 'purple'}>
                    {approval.type}
                  </Tag>
                </div>
                <div>
                  <Text strong>Customer:</Text> {approval.customerName ?? '—'}
                </div>
                <div>
                  <Text strong>Quantity:</Text> {approval.quantity ?? '—'}
                </div>
              </div>
            </div>

            <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Please select a reason' }]}>
              <Select placeholder="Select a reason">
                {reasonsFor(decisionMode).map((reason) => (
                  <Option key={reason} value={reason}>
                    {reason}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="remarks"
              label="Remarks"
              rules={remarksRequired ? [{ required: true, message: 'Please provide remarks' }] : []}
            >
              <TextArea rows={4} placeholder="Enter approval or rejection remarks..." />
            </Form.Item>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" type="button" onClick={() => setDecisionOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateApprovalMutation.isPending}>
                {decisionMode === 'Approved' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </Form>
        ) : (
          <div className="text-sm text-muted-foreground">Approval not found.</div>
        )}
      </Modal>
    </PageShell>
  )
}

export default ApprovalDetailPage

